import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { FaChevronLeft, FaChevronRight, FaPlus, FaClock, FaMapMarkerAlt, FaTrash, FaPencilAlt, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './AgendaManager.css';

const AgendaManager = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Helper to format date as YYYY-MM-DD in local time
    const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Form State
    const [showEventForm, setShowEventForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentEvent, setCurrentEvent] = useState({
        title: '',
        date: '',
        startTime: '08:00',
        endTime: '10:00',
        location: '',
        description: '',
        color: '#1890ff' // Default Blue
    });

    // Colors
    const eventColors = [
        '#1890ff', // Blue (Academic)
        '#52c41a', // Green (Activity)
        '#faad14', // Orange (Holiday)
        '#f5222d', // Red (Important)
        '#722ed1', // Purple (Meeting)
    ];

    // Fetch Events
    useEffect(() => {
        const q = query(collection(db, 'agenda'), orderBy('date', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const eventList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setEvents(eventList);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching agenda:", error);
            toast.error("Gagal memuat agenda.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Calendar Logic
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const days = new Date(year, month + 1, 0).getDate();
        return days;
    };

    const getFirstDayOfMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month, 1).getDay(); // 0 = Sunday
    };

    const changeMonth = (offset) => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
    };

    const handleDateClick = (day) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        setSelectedDate(newDate);
        // Format for input: YYYY-MM-DD
        const dateString = formatDateLocal(newDate);
        setCurrentEvent(prev => ({ ...prev, date: dateString }));
    };

    const getEventsForDay = (day) => {
        const targetDate = formatDateLocal(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
        return events.filter(e => e.date === targetDate);
    };

    const getEventsForSelectedDate = () => {
        const targetDate = formatDateLocal(selectedDate);
        return events.filter(e => e.date === targetDate);
    };

    // CRUD Operations
    const handleOpenAdd = () => {
        setIsEditing(false);
        setCurrentEvent({
            title: '',
            date: formatDateLocal(selectedDate),
            startTime: '08:00',
            endTime: '10:00',
            location: '',
            description: '',
            color: '#1890ff'
        });
        setShowEventForm(true);
    };

    const handleOpenEdit = (event) => {
        setIsEditing(true);
        setCurrentEvent(event);
        setShowEventForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Yakin ingin menghapus agenda ini?')) {
            try {
                await deleteDoc(doc(db, 'agenda', id));
                toast.success("Agenda dihapus.");
                if (showEventForm && currentEvent.id === id) setShowEventForm(false);
            } catch (err) {
                toast.error("Gagal menghapus agenda.");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await updateDoc(doc(db, 'agenda', currentEvent.id), currentEvent);
                toast.success("Agenda diperbarui!");
            } else {
                await addDoc(collection(db, 'agenda'), {
                    ...currentEvent,
                    createdAt: new Date().toISOString()
                });
                toast.success("Agenda ditambahkan!");
            }
            setShowEventForm(false);
        } catch (err) {
            console.error(err);
            toast.error("Gagal menyimpan agenda.");
        }
    };

    // Render Calendar Grid
    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate); // 0 (Sun) - 6 (Sat)

        const blanks = Array(firstDay).fill(null);
        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        const totalSlots = [...blanks, ...days];

        return (
            <div className="calendar-grid">
                {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(day => (
                    <div key={day} className="calendar-day-header">{day}</div>
                ))}

                {totalSlots.map((day, index) => {
                    if (!day) return <div key={`blank-${index}`} className="calendar-day empty"></div>;

                    const dateStr = formatDateLocal(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
                    const isToday = formatDateLocal(new Date()) === dateStr;
                    const isSelected = selectedDate.getDate() === day && selectedDate.getMonth() === currentDate.getMonth() && selectedDate.getFullYear() === currentDate.getFullYear();
                    const dayEvents = getEventsForDay(day);

                    return (
                        <div
                            key={day}
                            className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleDateClick(day)}
                        >
                            <div className="day-number">{day}</div>
                            <div className="day-events">
                                {dayEvents.map(ev => (
                                    <div key={ev.id} className="event-dot" style={{ backgroundColor: ev.color }}>
                                        {ev.title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render Loading
    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Memuat agenda...</div>;
    }

    return (
        <div className="agenda-container">
            {/* Calendar Section */}
            <div className="calendar-section">
                <div className="calendar-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button className="calendar-nav-btn" onClick={() => changeMonth(-1)}><FaChevronLeft /></button>
                        <h2>
                            {currentDate.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                        </h2>
                        <button className="calendar-nav-btn" onClick={() => changeMonth(1)}><FaChevronRight /></button>
                    </div>
                    <button className="add-new-btn" onClick={handleOpenAdd}>
                        <FaPlus /> Agenda Baru
                    </button>
                </div>

                {renderCalendar()}
            </div>

            {/* Detail / Event List Section */}
            <div className="events-section">
                <div className="events-header">
                    <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                        {selectedDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <span style={{ fontSize: '0.85rem', color: '#888' }}>
                        {getEventsForSelectedDate().length} Agenda
                    </span>
                </div>

                <div className="events-list">
                    {getEventsForSelectedDate().length > 0 ? (
                        getEventsForSelectedDate().map(ev => (
                            <div key={ev.id} className="event-card" style={{ borderLeftColor: ev.color }} onClick={() => handleOpenEdit(ev)}>
                                <div className="event-time">
                                    <FaClock size={12} /> {ev.startTime} - {ev.endTime}
                                </div>
                                <div className="event-title">{ev.title}</div>
                                {ev.location && (
                                    <div className="event-desc" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <FaMapMarkerAlt size={10} /> {ev.location}
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '8px' }}>
                                    {/* Edit Icon Button */}
                                    <button onClick={(e) => { e.stopPropagation(); handleOpenEdit(ev); }} style={{ background: 'none', border: 'none', color: '#1890ff', cursor: 'pointer', fontSize: '0.9rem' }} title="Edit"><FaPencilAlt /></button>
                                    {/* Delete Icon Button */}
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(ev.id); }} style={{ background: 'none', border: 'none', color: '#ff4d4f', cursor: 'pointer', fontSize: '0.9rem' }} title="Hapus"><FaTrash /></button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                            <p>Tidak ada agenda pada tanggal ini.</p>
                            <button style={{ color: '#1890ff', background: 'none', border: 'none', cursor: 'pointer', marginTop: '10px' }} onClick={handleOpenAdd}>
                                + Tambah Agenda
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Form */}
            {showEventForm && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '450px' }}>
                        <div className="admin-modal-header">
                            <h3>{isEditing ? 'Edit Agenda' : 'Tambah Agenda Baru'}</h3>
                            <button type="button" className="admin-modal-close" onClick={() => setShowEventForm(false)}><FaTimes /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="modern-form">
                            <div className="admin-modal-body">
                                <div className="form-group">
                                    <label>Judul Agenda</label>
                                    <input type="text" value={currentEvent.title} onChange={e => setCurrentEvent({ ...currentEvent, title: e.target.value })} required placeholder="Contoh: Rapat Guru" />
                                </div>

                                <div className="form-group">
                                    <label>Tanggal</label>
                                    <input type="date" value={currentEvent.date} onChange={e => setCurrentEvent({ ...currentEvent, date: e.target.value })} required />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="form-group">
                                        <label>Mulai</label>
                                        <input type="time" value={currentEvent.startTime} onChange={e => setCurrentEvent({ ...currentEvent, startTime: e.target.value })} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Selesai</label>
                                        <input type="time" value={currentEvent.endTime} onChange={e => setCurrentEvent({ ...currentEvent, endTime: e.target.value })} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Lokasi (Opsional)</label>
                                    <input type="text" value={currentEvent.location} onChange={e => setCurrentEvent({ ...currentEvent, location: e.target.value })} placeholder="Contoh: Ruang Guru" />
                                </div>

                                <div className="form-group">
                                    <label>Warna Label</label>
                                    <div className="color-picker">
                                        {eventColors.map(color => (
                                            <div
                                                key={color}
                                                className={`color-option ${currentEvent.color === color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setCurrentEvent({ ...currentEvent, color })}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Deskripsi</label>
                                    <textarea value={currentEvent.description} onChange={e => setCurrentEvent({ ...currentEvent, description: e.target.value })} rows="3"></textarea>
                                </div>
                            </div>
                            <div className="admin-modal-footer">
                                <button type="button" className="cancel-btn" onClick={() => setShowEventForm(false)}>Batal</button>
                                <button type="submit" className="save-btn">{isEditing ? 'Update' : 'Simpan'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgendaManager;
